import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backend';

const API = BACKEND_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    if (data.token) localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/api/auth/register`, { name, email, password }, { withCredentials: true });
    if (data.token) localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const updateProfile = async (name, email) => {
    const token = localStorage.getItem('token');
    const { data } = await axios.put(`${API}/api/auth/profile`, { name, email }, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    setUser((current) => ({ ...(current || {}), ...data }));
    return data;
  };

  const changePassword = async (currentPassword, newPassword, newPasswordConfirmation) => {
    const token = localStorage.getItem('token');
    const { data } = await axios.put(`${API}/api/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    }, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
    return data;
  };

  const resetPassword = async (email, token, password, passwordConfirmation) => {
    const { data } = await axios.post(`${API}/api/auth/reset-password`, {
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
    }, { withCredentials: true });

    return data;
  };

  const logout = async () => {
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/api/auth/logout`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch {}
    localStorage.removeItem('token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, updateProfile, changePassword, forgotPassword, resetPassword, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
